import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { DICTATION_COURSE_TYPE_TEXT } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type DictationCourse = {
  id: string;
  name?: string;
  courseName?: string;
  displayOrder: number;
  level: number;
  description?: string;
  courseDescription?: string;
  type?: string;
  note?: string;
  totalLesson?: number;
  hasCategories?: boolean;
};

type DictationCategory = {
  id: string;
  name: string;
  courseId: string;
  displayOrder: number;
  chapters?: string[];
};

type SampleSentence = {
  en: string;
  ja: string;
};

type DictationLesson = {
  id: string;
  name: string;
  categoryId?: string;
  chapter?: string;
  displayOrder: number;
  description?: string;
  sampleDescription?: string;
};

const categorySchema = yup.object().shape({
  id: yup.string().required("カテゴリIDが必須です。"),
  name: yup.string().required("カテゴリ名が必須です。"),
});

const lessonSchema = yup.object().shape({
  id: yup.string().required("レッスンIDが必須です。"),
  name: yup.string().required("レッスン名が必須です。"),
  categoryId: yup.string(),
  chapter: yup.string(),
  description: yup.string(),
  sampleDescription: yup.string(),
});

const DictationCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  
  const [course, setCourse] = useState<DictationCourse | null>(null);
  const [categories, setCategories] = useState<DictationCategory[]>([]);
  const [lessons, setLessons] = useState<DictationLesson[]>([]);
  const [activeCategory, setActiveCategory] = useState<DictationCategory | null>(null);
  const [activeLesson, setActiveLesson] = useState<DictationLesson | null>(null);
  const [isEditCategoryMode, setIsEditCategoryMode] = useState(false);
  const [isEditLessonMode, setIsEditLessonMode] = useState(false);
  const [chapters, setChapters] = useState<string[]>([""]);  // State for managing chapters list
  const [availableChapters, setAvailableChapters] = useState<string[]>([]); // Available chapters from categories
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(""); // Currently selected category
  const [filteredChapters, setFilteredChapters] = useState<string[]>([]); // Chapters filtered by selected category
  const [sampleSentences, setSampleSentences] = useState<SampleSentence[]>([]); // Sample sentences for lesson modal

  const {
    register: registerCategory,
    handleSubmit: handleSubmitCategory,
    formState: { errors: categoryErrors },
    reset: resetCategory,
  } = useForm({
    resolver: yupResolver(categorySchema),
  });

  const {
    register: registerLesson,
    handleSubmit: handleSubmitLesson,
    formState: { errors: lessonErrors },
    reset: resetLesson,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(lessonSchema),
  });

  const fetchCourse = async () => {
    if (!id) return;
    
    dispatch(setIsLoading(true));
    try {
      const response = await API.get(`dictation-course/${id}`);
      const { data } = response.data;
      setCourse(data);
    } catch (err) {
      console.error(err);
      toast.error("コースの取得に失敗しました。");
      navigate("/content/dictation-courses");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchCategories = async () => {
    if (!id) return;
    
    try {
      const response = await API.get(`dictation-categories?courseId=${id}`);
      const { data } = response.data;
      setCategories(data || []);
    } catch (err) {
      console.error(err);
      toast.error("カテゴリの取得に失敗しました。");
    }
  };

  const fetchLessons = async () => {
    if (!id) return;
    
    try {
      const response = await API.get(`dictation-lessons?courseId=${id}`);
      const { data } = response.data;
      setLessons(data || []);
    } catch (err) {
      console.error(err);
      toast.error("レッスンの取得に失敗しました。");
    }
  };

  // Chapter management functions
  const addChapter = () => {
    setChapters([...chapters, ""]);
  };

  const removeChapter = (index: number) => {
    if (chapters.length > 1) {
      const newChapters = chapters.filter((_, i) => i !== index);
      setChapters(newChapters);
    }
  };

  const updateChapter = (index: number, value: string) => {
    const newChapters = [...chapters];
    newChapters[index] = value;
    setChapters(newChapters);
  };

  // Sample sentence management functions
  const addSampleSentence = () => {
    setSampleSentences([...sampleSentences, { en: "", ja: "" }]);
  };

  const removeSampleSentence = (index: number) => {
    setSampleSentences(sampleSentences.filter((_, i) => i !== index));
  };

  const updateSampleSentence = (index: number, field: "en" | "ja", value: string) => {
    const updated = [...sampleSentences];
    updated[index] = { ...updated[index], [field]: value };
    setSampleSentences(updated);
  };

  // Extract available chapters from categories
  const updateAvailableChapters = () => {
    const allChapters: string[] = [];
    categories.forEach(category => {
      if (category.chapters && Array.isArray(category.chapters)) {
        allChapters.push(...category.chapters);
      }
    });
    // Remove duplicates and empty values using filter and indexOf
    const filteredChapters = allChapters.filter(chapter => chapter.trim() !== "");
    const uniqueChapters = filteredChapters.filter((chapter, index) => 
      filteredChapters.indexOf(chapter) === index
    );
    setAvailableChapters(uniqueChapters);
  };

  // Filter chapters based on selected category
  const updateFilteredChapters = (categoryId: string) => {
    if (!categoryId) {
      setFilteredChapters([]);
      return;
    }
    
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    if (selectedCategory && selectedCategory.chapters && Array.isArray(selectedCategory.chapters)) {
      const categoryChapters = selectedCategory.chapters.filter(chapter => chapter.trim() !== "");
      setFilteredChapters(categoryChapters);
    } else {
      setFilteredChapters([]);
    }
  };

  // Handle category selection change
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    updateFilteredChapters(categoryId);
    
    // Reset chapter selection when category changes
    const currentFormData = getValues();
    setValue("chapter", "");
  };

  const handleCreateCategory = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      
      const categoryData = {
        ...data,
        courseId: id,
        chapters: chapters.filter(chapter => chapter.trim() !== "") // Use chapters state
      };
      
      await API.post("dictation-category", categoryData);
      toast.success("カテゴリが作成されました。");
      fetchCategories();
      resetCategory();
      document.querySelector<HTMLButtonElement>("#modalCategory .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateCategory = async (data: any) => {
    if (!activeCategory) return;
    
    try {
      dispatch(setIsApiLoading(true));
      
      const categoryData = {
        ...data,
        courseId: id,
        chapters: chapters.filter(chapter => chapter.trim() !== "") // Use chapters state
      };
      
      await API.put(`dictation-category/${activeCategory.id}`, categoryData);
      toast.success("カテゴリが更新されました。");
      fetchCategories();
      resetCategory();
      document.querySelector<HTMLButtonElement>("#modalCategory .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteCategory = async (category: DictationCategory) => {
    if (!window.confirm(`「${category.name}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`dictation-category/${category.id}`);
      toast.success("カテゴリが削除されました。");
      fetchCategories();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleCreateLesson = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));

      const lessonData = {
        ...data,
        courseId: id,
        sentences: sampleSentences,
      };

      await API.post("dictation-lesson", lessonData);
      toast.success("レッスンが作成されました。");
      fetchLessons();
      resetLesson();
      document.querySelector<HTMLButtonElement>("#modalLesson .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateLesson = async (data: any) => {
    if (!activeLesson) return;

    try {
      dispatch(setIsApiLoading(true));

      const lessonData = {
        ...data,
        courseId: id,
        sentences: sampleSentences,
      };

      await API.put(`dictation-lesson/${activeLesson.id}`, lessonData);
      toast.success("レッスンが更新されました。");
      fetchLessons();
      resetLesson();
      document.querySelector<HTMLButtonElement>("#modalLesson .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteLesson = async (lesson: DictationLesson) => {
    if (!window.confirm(`「${lesson.name}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`dictation-lesson/${lesson.id}`);
      toast.success("レッスンが削除されました。");
      fetchLessons();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreateCategoryModal = () => {
    setIsEditCategoryMode(false);
    setActiveCategory(null);
    setChapters([""]); // Initialize with one empty chapter
    resetCategory({
      id: "",
      name: "",
    });
  };

  const openEditCategoryModal = (category: DictationCategory) => {
    setIsEditCategoryMode(true);
    setActiveCategory(category);
    setChapters(category.chapters && category.chapters.length > 0 ? category.chapters : [""]); // Initialize chapters state
    resetCategory({
      id: category.id,
      name: category.name,
    });
  };

  const openCreateLessonModal = () => {
    setIsEditLessonMode(false);
    setActiveLesson(null);
    setSampleSentences([]);

    // Only reset category/chapter related state if course has categories
    if (course?.hasCategories) {
      setSelectedCategoryId("");
      setFilteredChapters([]);
    }

    resetLesson({
      id: "",
      name: "",
      categoryId: course?.hasCategories ? "" : undefined,
      chapter: course?.hasCategories ? "" : undefined,
      description: "",
      sampleDescription: "",
    });
  };

  const openEditLessonModal = async (lesson: DictationLesson) => {
    setIsEditLessonMode(true);
    setActiveLesson(lesson);

    // Only handle category/chapter logic if course has categories
    if (course?.hasCategories) {
      const categoryId = lesson.categoryId || "";
      setSelectedCategoryId(categoryId);
      updateFilteredChapters(categoryId);
    }

    resetLesson({
      id: lesson.id,
      name: lesson.name,
      categoryId: course?.hasCategories ? (lesson.categoryId || "") : undefined,
      chapter: course?.hasCategories ? (lesson.chapter || "") : undefined,
      description: lesson.description || "",
      sampleDescription: lesson.sampleDescription || "",
    });

    // Fetch fresh sample sentences from dictationSampleSentences
    try {
      const response = await API.get(`dictation-sample-sentences?lessonId=${lesson.id}`);
      const { data } = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const sampleDoc = data[0];
        setValue("sampleDescription", sampleDoc.description || "");
        setSampleSentences(Array.isArray(sampleDoc.sentences) ? sampleDoc.sentences : []);
      } else {
        setValue("sampleDescription", "");
        setSampleSentences([]);
      }
    } catch (err) {
      console.error("Error fetching sample sentences:", err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCourse();
    }
  }, [id]);

  useEffect(() => {
    if (course) {
      if (course.hasCategories) {
        fetchCategories();
        fetchLessons(); // Also fetch lessons for courses with categories
      } else {
        fetchLessons();
      }
    }
  }, [course]);

  // Update available chapters when categories change
  useEffect(() => {
    updateAvailableChapters();
  }, [categories]);

  // Setup event listeners for categories
  useEffect(() => {
    const handleCategoryClick = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".edit-category-btn")) {
        const categoryId = target.closest(".edit-category-btn")?.getAttribute("data-id");
        const category = categories.find((c) => c.id === categoryId);
        if (category) {
          openEditCategoryModal(category);
        }
      }
      
      if (target.closest(".delete-category-btn")) {
        const categoryId = target.closest(".delete-category-btn")?.getAttribute("data-id");
        const category = categories.find((c) => c.id === categoryId);
        if (category) {
          handleDeleteCategory(category);
        }
      }
    };

    document.addEventListener("click", handleCategoryClick);
    
    return () => {
      document.removeEventListener("click", handleCategoryClick);
    };
  }, [categories]);

  // Setup event listeners for lessons
  useEffect(() => {
    const handleLessonClick = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".edit-lesson-btn")) {
        const lessonId = target.closest(".edit-lesson-btn")?.getAttribute("data-id");
        const lesson = lessons.find((l) => l.id === lessonId);
        if (lesson) {
          openEditLessonModal(lesson);
        }
      }
      
      if (target.closest(".delete-lesson-btn")) {
        const lessonId = target.closest(".delete-lesson-btn")?.getAttribute("data-id");
        const lesson = lessons.find((l) => l.id === lessonId);
        if (lesson) {
          handleDeleteLesson(lesson);
        }
      }
    };

    document.addEventListener("click", handleLessonClick);
    
    return () => {
      document.removeEventListener("click", handleLessonClick);
    };
  }, [lessons]);

  if (!course && !loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              コースが見つかりません。
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>ディクテーションコース詳細</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="mb-1">ディクテーションコース詳細</h4>
                <p className="text-muted mb-0">{course?.courseName || course?.name}</p>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/content/dictation-courses")}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  コース一覧に戻る
                </button>
              </div>
            </div>

            <Loading />
            {!loading && course && (
              <>
                {/* Card 1: Course Details */}
                <div className="card mb-4" style={{ height: 'auto' }}>
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      ディクテーションコース詳細
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card border-info">
                          <div className="card-body">
                            <h6 className="card-title text-info">
                              <i className="fas fa-info-circle me-2"></i>
                              コース情報
                            </h6>
                            <table className="table table-sm">
                              <tbody>
                                <tr>
                                  <td><strong>コース名:</strong></td>
                                  <td>{course.courseName || course.name}</td>
                                </tr>
                                <tr>
                                  <td><strong>表示順:</strong></td>
                                  <td>{course.displayOrder}</td>
                                </tr>
                                <tr>
                                  <td><strong>レベル:</strong></td>
                                  <td>{course.level}</td>
                                </tr>
                                <tr>
                                  <td><strong>タイプ:</strong></td>
                                  <td>{DICTATION_COURSE_TYPE_TEXT[course.type] || course.type}</td>
                                </tr>
                                <tr>
                                  <td><strong>総レッスン数:</strong></td>
                                  <td>{course.totalLesson}</td>
                                </tr>
                                <tr>
                                  <td><strong>カテゴリ有無:</strong></td>
                                  <td>
                                    <span className={`badge ${course.hasCategories ? 'bg-success' : 'bg-secondary'}`}>
                                      {course.hasCategories ? 'あり' : 'なし'}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card border-secondary">
                          <div className="card-body">
                            <h6 className="card-title text-secondary">
                              <i className="fas fa-file-text me-2"></i>
                              説明
                            </h6>
                            <p className="card-text">
                              {course.courseDescription || course.description || "説明がありません。"}
                            </p>
                            {course.note && (
                              <>
                                <h6 className="card-title text-secondary mt-3">
                                  <i className="fas fa-sticky-note me-2"></i>
                                  備考
                                </h6>
                                <p className="card-text">{course.note}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Category Management (only for courses with categories) */}
                {course.hasCategories && (
                  <div className="card mb-4" style={{ height: 'auto' }}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-folder me-2"></i>
                        カテゴリ管理
                      </h5>
                      <button
                        className="btn btn-primary btn-sm"
                        data-bs-toggle="modal"
                        data-bs-target="#modalCategory"
                        onClick={openCreateCategoryModal}
                      >
                        <i className="fas fa-plus me-2"></i>
                        カテゴリ追加
                      </button>
                    </div>
                    <div className="card-body">
                      {categories.length > 0 ? (
                        <div className="table-responsive">
                          <DataTable
                            data={categories}
                            className="display"
                            options={{
                              autoWidth: true,
                              columnDefs: [
                                { targets: "_all", className: "text-start" },
                              ],
                              stripeClasses: [],
                              order: [[0, "asc"]], // Sort by category name
                              searching: true,
                              destroy: true,
                              info: true,
                              paging: true,
                              language: {
                                lengthMenu: "レコード数&nbsp;_MENU_",
                                emptyTable: "データがありません。",
                                search: "検索:",
                                paginate: {
                                  first: "最初",
                                  last: "最後",
                                  next: "次へ",
                                  previous: "前へ",
                                },
                              },
                            }}
                            columns={[
                              {
                                title: "ID",
                                data: "id",
                                className: "text-start",
                                orderable: true,
                                width: "100px",
                              },
                              {
                                title: "カテゴリ名",
                                data: "name",
                                className: "text-start",
                                orderable: true,
                              },
                              {
                                title: "チャプター",
                                data: "chapters",
                                className: "text-start",
                                orderable: false,
                                render: (data) => {
                                  if (!data || !Array.isArray(data)) return "-";
                                  return data.map(chapter => `<div>${chapter}</div>`).join("");
                                },
                              },
                              {
                                title: "操作",
                                data: null,
                                orderable: false,
                                className: "text-center",
                                render: (data, type, row) => {
                                  return `
                                    <div class="d-flex justify-content-center gap-2">
                                      <button class="btn btn-sm btn-outline-primary edit-category-btn" data-bs-toggle="modal" data-bs-target="#modalCategory" data-id="${row.id}">
                                        <i class="fas fa-edit"></i>
                                      </button>
                                      <button class="btn btn-sm btn-outline-danger delete-category-btn" data-id="${row.id}">
                                        <i class="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  `;
                                },
                              },
                            ]}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">カテゴリがありません。</p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#modalCategory"
                            onClick={openCreateCategoryModal}
                          >
                            <i className="fas fa-plus me-2"></i>
                            最初のカテゴリを作成
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Card 3: Lesson Management (always shown) */}
                <div className="card" style={{ height: 'auto' }}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-list me-2"></i>
                      レッスン管理
                    </h5>
                    <button
                      className="btn btn-primary btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#modalLesson"
                      onClick={openCreateLessonModal}
                    >
                      <i className="fas fa-plus me-2"></i>
                      レッスン追加
                    </button>
                  </div>
                  <div className="card-body">
                    {lessons.length > 0 ? (
                      <div className="table-responsive">
                        <DataTable
                          data={lessons}
                          className="display"
                          options={{
                            autoWidth: true,
                            columnDefs: [
                              { targets: "_all", className: "text-start" },
                            ],
                            stripeClasses: [],
                            order: [[0, "asc"]], // Sort by lesson name
                            searching: true,
                            destroy: true,
                            info: true,
                            paging: true,
                            language: {
                              lengthMenu: "レコード数&nbsp;_MENU_",
                              emptyTable: "データがありません。",
                              search: "検索:",
                              paginate: {
                                first: "最初",
                                last: "最後",
                                next: "次へ",
                                previous: "前へ",
                              },
                            },
                          }}
                          columns={[
                            {
                              title: "ID",
                              data: "id",
                              className: "text-start",
                              orderable: true,
                              width: "100px",
                            },
                            {
                              title: "レッスン名",
                              data: "name",
                              className: "text-start",
                              orderable: true,
                            },
                            ...(course.hasCategories ? [
                              {
                                title: "カテゴリ",
                                data: "categoryName",
                                className: "text-start",
                                orderable: true,
                                render: (data) => data || "-",
                              },
                              {
                                title: "チャプター",
                                data: "chapter",
                                className: "text-start",
                                orderable: true,
                                render: (data) => data || "-",
                              }
                            ] : []),
                            {
                              title: "タイトル",
                              data: "description",
                              className: "text-start",
                              orderable: false,
                              render: (data) => data || "-",
                            },
                            {
                              title: "操作",
                              data: null,
                              orderable: false,
                              className: "text-center",
                              render: (data, type, row) => {
                                return `
                                  <div class="d-flex justify-content-center gap-2">
                                    <button class="btn btn-sm btn-outline-primary edit-lesson-btn" data-bs-toggle="modal" data-bs-target="#modalLesson" data-id="${row.id}">
                                      <i class="fas fa-edit"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-danger delete-lesson-btn" data-id="${row.id}">
                                      <i class="fas fa-trash"></i>
                                    </button>
                                  </div>
                                `;
                              },
                            },
                          ]}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">レッスンがありません。</p>
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#modalLesson"
                          onClick={openCreateLessonModal}
                        >
                          <i className="fas fa-plus me-2"></i>
                          最初のレッスンを作成
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Category Modal */}
        <Modal
          id="modalCategory"
          title={isEditCategoryMode ? "カテゴリ編集" : "カテゴリ作成"}
        >
          <Form onSubmit={handleSubmitCategory(isEditCategoryMode ? handleUpdateCategory : handleCreateCategory)}>
            <Form.Group className="mb-3">
              <Form.Label>カテゴリID <span className="text-danger">*</span></Form.Label>
              {isEditCategoryMode ? (
                <div className="form-control-plaintext">
                  <strong>{activeCategory?.id}</strong>
                </div>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="カテゴリIDを入力"
                  isInvalid={!!categoryErrors.id}
                  {...registerCategory("id")}
                />
              )}
              {!isEditCategoryMode && (
                <Form.Control.Feedback type="invalid">
                  {categoryErrors.id?.message}
                </Form.Control.Feedback>
              )}
              {isEditCategoryMode && (
                <Form.Text className="text-muted">
                  編集時はIDを変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>カテゴリ名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="カテゴリ名を入力"
                isInvalid={!!categoryErrors.name}
                {...registerCategory("name")}
              />
              <Form.Control.Feedback type="invalid">
                {categoryErrors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>チャプター</Form.Label>
              <div className="chapters-list">
                {chapters.map((chapter, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="text"
                      placeholder={`チャプター ${index + 1}`}
                      value={chapter}
                      onChange={(e) => updateChapter(index, e.target.value)}
                      className="me-2"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm me-2"
                      onClick={() => removeChapter(index)}
                      disabled={chapters.length === 1}
                      title="チャプターを削除"
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    {index === chapters.length - 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={addChapter}
                        title="チャプターを追加"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <Form.Text className="text-muted">
                + ボタンでチャプターを追加、- ボタンで削除できます。
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditCategoryMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>

        {/* Lesson Modal */}
        <Modal
          id="modalLesson"
          title={isEditLessonMode ? "レッスン編集" : "レッスン作成"}
        >
          <Form onSubmit={handleSubmitLesson(isEditLessonMode ? handleUpdateLesson : handleCreateLesson)}>
            <Form.Group className="mb-3">
              <Form.Label>レッスンID <span className="text-danger">*</span></Form.Label>
              {isEditLessonMode ? (
                <div className="form-control-plaintext">
                  <strong>{activeLesson?.id}</strong>
                </div>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="レッスンIDを入力"
                  isInvalid={!!lessonErrors.id}
                  {...registerLesson("id")}
                />
              )}
              {!isEditLessonMode && (
                <Form.Control.Feedback type="invalid">
                  {lessonErrors.id?.message}
                </Form.Control.Feedback>
              )}
              {isEditLessonMode && (
                <Form.Text className="text-muted">
                  編集時はIDを変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>レッスン名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="レッスン名を入力"
                isInvalid={!!lessonErrors.name}
                {...registerLesson("name")}
              />
              <Form.Control.Feedback type="invalid">
                {lessonErrors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {course?.hasCategories && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>カテゴリ</Form.Label>
                  <Form.Select
                    {...registerLesson("categoryId")}
                    onChange={(e) => {
                      const categoryId = e.target.value;
                      setValue("categoryId", categoryId);
                      handleCategoryChange(categoryId);
                    }}
                    value={selectedCategoryId}
                  >
                    <option value="">カテゴリを選択してください</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>チャプター</Form.Label>
                  <Form.Select
                    {...registerLesson("chapter")}
                    disabled={!selectedCategoryId}
                  >
                    <option value="">
                      {selectedCategoryId ? "チャプターを選択してください" : "まずカテゴリを選択してください"}
                    </option>
                    {filteredChapters.map((chapter, index) => (
                      <option key={index} value={chapter}>
                        {chapter}
                      </option>
                    ))}
                  </Form.Select>
                  {!selectedCategoryId && (
                    <Form.Text className="text-muted">
                      チャプターを選択するには、まずカテゴリを選択してください。
                    </Form.Text>
                  )}
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Label>タイトル</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="レッスンのタイトルを入力"
                {...registerLesson("description")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>サンプル説明</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="サンプルの説明を入力"
                {...registerLesson("sampleDescription")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>サンプル文一覧</Form.Label>
              {sampleSentences.length === 0 && (
                <p className="text-muted small mb-2">サンプル文がありません。</p>
              )}
              {sampleSentences.map((sentence, index) => (
                <div key={index} className="border rounded p-2 mb-2 bg-light">
                  <div className="d-flex align-items-start gap-2">
                    <div className="flex-grow-1">
                      <Form.Control
                        type="text"
                        placeholder="English sentence"
                        value={sentence.en}
                        onChange={(e) => updateSampleSentence(index, "en", e.target.value)}
                        className="mb-1"
                        size="sm"
                      />
                      <Form.Control
                        type="text"
                        placeholder="日本語訳"
                        value={sentence.ja}
                        onChange={(e) => updateSampleSentence(index, "ja", e.target.value)}
                        size="sm"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeSampleSentence(index)}
                      title="削除"
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addSampleSentence}
              >
                <i className="fas fa-plus me-1"></i>
                文を追加
              </button>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditLessonMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default DictationCourseDetail;